import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NzModalService } from 'ng-zorro-antd/modal';

import { BidCondition } from '../../../../graphql/service/graphql-auto-main-service';

@Component({
  selector: 'app-bid-condition-selector',
  templateUrl: './bid-condition-selector.component.html',
  styleUrls: ['./bid-condition-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BidConditionSelectorComponent),
      multi: true,
    },
  ],
})
export class BidConditionSelectorComponent implements ControlValueAccessor {
  @Input() isRussianClient = false;
  @Input()
  set conditions(conditions: BidCondition[] | null) {
    this.bidConditions = conditions || [];
    this.setDisassemblyState();
  }

  @Output()
  disassemblyStateChange = new EventEmitter<boolean>();

  value: number | null = null;
  disabled = false;

  bidConditions: BidCondition[] = [];
  private previousConditionId: number | null = null;
  private confirmedDisassemblyConditionId: number | null = null;

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private modalService: NzModalService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  writeValue(conditionId: number | null): void {
    this.value = this.normalizeConditionId(conditionId);
    this.previousConditionId = this.value;
    this.confirmedDisassemblyConditionId = null;
    this.setDisassemblyState();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  markAsTouched(): void {
    this.onTouched();
  }

  get isDisassemblySelected(): boolean {
    return !!this.confirmedDisassemblyConditionId;
  }

  handleConditionChange(conditionId: number): void {
    this.onTouched();

    const normalizedConditionId = this.normalizeConditionId(conditionId);
    const selectedCondition = this.getConditionById(normalizedConditionId);

    if (selectedCondition) {
      this.value = normalizedConditionId;

      if (selectedCondition.isDisassembly) {
        this.confirmDisassemblyCondition(normalizedConditionId, selectedCondition.title);
      } else {
        this.confirmedDisassemblyConditionId = null;
        this.acceptCondition(normalizedConditionId);
      }
    } else {
      this.acceptCondition(normalizedConditionId);
    }

    this.cdr.markForCheck();
  }

  private confirmDisassemblyCondition(conditionId: number, conditionTitle: string): void {
    if (this.confirmedDisassemblyConditionId === conditionId) {
      this.acceptCondition(conditionId);
    } else {
      this.modalService.confirm({
        nzCentered: true,
        nzTitle: this.translate.instant('LOT_BIDDING_MODAL.DISASSEMBLY_CONDITION_CONFIRM_TITLE', {
          conditionTitle: this.translate.instant(conditionTitle),
        }),
        nzContent: this.translate.instant(
          'LOT_BIDDING_MODAL.DISASSEMBLY_CONDITION_CONFIRM_CONTENT',
        ),
        nzOkText: this.translate.instant('LOT_BIDDING_MODAL.DISASSEMBLY_CONDITION_CONFIRM_OK'),
        nzCancelText: this.translate.instant('CANCEL'),
        nzOnOk: () => {
          this.confirmedDisassemblyConditionId = conditionId;
          this.acceptCondition(conditionId);
          this.cdr.markForCheck();
        },
        nzOnCancel: () => {
          this.rollbackConditionChange();
          this.cdr.markForCheck();
        },
      });
    }
  }

  private acceptCondition(conditionId: number | null): void {
    this.value = conditionId;
    this.previousConditionId = conditionId;

    this.onChange(conditionId);
    this.setDisassemblyState();
    this.cdr.markForCheck();
  }

  private rollbackConditionChange(): void {
    this.value = this.previousConditionId;

    this.setDisassemblyState();
    this.cdr.markForCheck();
  }

  private setDisassemblyState(): void {
    const selectedCondition = this.getConditionById(this.value);
    this.disassemblyStateChange.emit(selectedCondition?.isDisassembly || false);
  }

  private getConditionById(conditionId: number | null): BidCondition | undefined {
    if (conditionId === null) {
      return undefined;
    } else {
      return this.bidConditions.find((condition) => +condition.id === +conditionId);
    }
  }

  private normalizeConditionId(conditionId: number | string | null): number | null {
    if (conditionId === null) {
      return null;
    } else {
      return +conditionId;
    }
  }
}
